/**
 * Redux actions for saved items.
 * @module redux/actions/savedItems
 */

import { createAction } from 'redux-actions';
import uuid from 'uuid';
import { push } from 'react-router-redux';
import { getAssets as apiGetAssets } from '../api/camio';
import {
  getFolders as apiGetSavedItemFolders,
  addFolder as apiAddSavedItemFolder,
  removeFolder as apiRemoveSavedItemFolder,
  createSavedItem as apiCreateSavedItem,
  updateFolder as apiUpdateFolder,
  deleteSavedItem as apiDeleteSavedItem
} from '../api/savedItems';
import AssetSearchUtils from '../util/assetSearchUtils';
import { getLuciPanel } from './luciPanel';
import TemplateUtils from '../util/templateUtils';
import { actions as snackActions } from './snackbar';
import {
  actions as assetActions,
  templateDefinitionLoaded,
  loadChannelListByAssetId,
  ASSET_SELECTED
} from './asset';

export const CONTEXT_CHANGED = 'SAVED_ITEMS/CONTEXT_CHANGED';
export const LOAD_SAVED_ITEM = 'SAVED_ITEMS/LOAD_SAVED_ITEM';
export const SAVED_ITEM_LOADED = 'SAVED_ITEMS/SAVED_ITEM_LOADED';
export const LOAD_SAVED_ITEM_FAILED = 'SAVED_ITEMS/LOAD_SAVED_ITEM_FAILED';
export const CLEAR_SAVED_ITEMS = 'SAVED_ITEMS/CLEAR_SAVED_ITEMS';
export const REQUEST_FOLDERS = 'SAVED_ITEMS/REQUEST_FOLDERS';
export const RECEIVED_FOLDERS = 'SAVED_ITEMS/RECEIVED_FOLDERS';
export const FOLDERS_REQUEST_FAILED = 'SAVED_ITEMS/FOLDERS_REQUEST_FAILED';
export const FOLDER_SELECTED = 'SAVED_ITEMS/FOLDERS_SELECTED';
export const FOLDER_REMOVED = 'SAVED_ITEMS/FOLDER_REMOVED';
export const SORT_BY_CHANGED = 'SAVED_ITEMS/SORT_BY_CHANGED';
export const BEGIN_SAVED_ITEMS_REQUEST = 'SAVED_ITEMS/BEGIN_SAVED_ITEMS_REQUEST';
export const RECEIVED_SAVED_ITEMS = 'SAVED_ITEMS/RECEIVED_SAVED_ITEMS';
export const SAVED_ITEMS_REQUEST_FAILED = 'SAVED_ITEMS/SAVED_ITEMS_REQUEST_FAILED';
export const PAGE_CHANGED = 'SAVED_ITEMS/PAGE_CHANGED';
export const RELOAD = 'SAVED_ITEMS/RELOAD';
export const SAVED_ITEM_REMOVED = 'SAVED_ITEMS/SAVED_ITEM_REMOVED';
export const SEARCH_TERMS_CHANGED = 'SAVED_ITEMS/SEARCH_TERMS_CHANGED';

const handleSavedItemsRequestFailed = createAction(SAVED_ITEMS_REQUEST_FAILED);

const changeSortBy = sortBy => createAction(SORT_BY_CHANGED)({ sortBy });

export const changeSearchTerms = searchTerms => createAction(SEARCH_TERMS_CHANGED)({ searchTerms });

const selectFolder = folderId => createAction(FOLDER_SELECTED)({ folderId });

export const loadSavedItem = id => (dispatch, getState) => {
  const selectedItem = getState().savedItems.savedItems.find(s => s.id === id);
  if (selectedItem) {
    dispatch(createAction(LOAD_SAVED_ITEM)());
    Promise.all([
      dispatch(createAction(ASSET_SELECTED)({ selectedAsset: selectedItem.mosObject })),
      dispatch(templateDefinitionLoaded(selectedItem.templateDefinition)),
      dispatch(loadChannelListByAssetId(selectedItem.mosObject.id)),
      dispatch(getLuciPanel(selectedItem.mosObject.id))
    ])
      .then(() => {
        dispatch(createAction(SAVED_ITEM_LOADED)());
        return dispatch(push(`/editor/${selectedItem.mosObject.id}`));
      })
      .catch(() => {
        dispatch(createAction(LOAD_SAVED_ITEM_FAILED)());
        dispatch(
          snackActions.showSnack(uuid.v4(), {
            content: 'Failed to load asset.',
            timeout: 7000,
            buttonTemplate: 'DISMISS'
          })
        );
      });
  }
};

const addFolder = (name, folder) => dispatch =>
  apiAddSavedItemFolder(name, folder.id, folder.owner, folder.expirationHours).then(
    () => dispatch(getSavedItemFolders()),
    error => {
      console.error(error);
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: `Error while adding the folder: ${name}`,
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    }
  );

const updateFolder = name => (dispatch, getState) => {
  const folderId = getState().savedItems.selectedFolder;
  const folder = getState().savedItems.folders[folderId];

  return apiUpdateFolder(
    folderId,
    name,
    folder.parentFolderId,
    folder.owner,
    folder.expirationHours
  ).then(
    () => dispatch(getSavedItemFolders()),
    error => {
      console.error(error);
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: `Error while renaming the folder: ${folder.name} -> ${name}`,
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    }
  );
};

const removeFolder = folderId => (dispatch, getState) => {
  const folderName = getState().savedItems.folders[folderId].name;
  return apiRemoveSavedItemFolder(folderId).then(
    () => {
      dispatch(createAction(FOLDER_REMOVED)({ folderId }));
      return dispatch(getSavedItemFolders());
    },
    error => {
      console.error(error);
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: `Error while deleting the folder: ${folderName}`,
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    }
  );
};

const createSavedItem = (mosObjectId, updateExisting) => dispatch => {
  if (mosObjectId) {
    if (updateExisting) {
      return dispatch(assetActions.updateMosObject({ callback: createSavedItemInternal }));
    }
    return dispatch(assetActions.createMosObjectFromExisting({ callback: createSavedItemInternal }));
  }
  return dispatch(assetActions.createNewMosObject({ callback: createSavedItemInternal }));
};

const createSavedItemInternal = mosObjectId => (dispatch, getState) => {
  const currentState = getState().savedItems;
  const folderId = currentState.selectedFolder;
  if (!folderId || !mosObjectId) {
    return; // Display error
  }

  const owner = currentState.folders[folderId].owner;
  return apiCreateSavedItem(mosObjectId, folderId, owner)
    .then(response => {
      if (response.error) {
        return dispatch(
          snackActions.showSnack(uuid.v4(), {
            content: 'Failed to create saved item.',
            timeout: 7000,
            buttonTemplate: 'DISMISS'
          })
        );
      }
      return dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: 'Successfully saved item.',
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    })
    .catch(error => {
      console.error('Error while creating saved item.');
      console.error(error);
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: 'Failed to create saved item.',
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    });
};

const changeContext = contextId => createAction(CONTEXT_CHANGED)({ contextId });

const clearSavedItems = () => createAction(CLEAR_SAVED_ITEMS);

const changePage = page => createAction(PAGE_CHANGED)({ page });

const deleteSavedItem = id => dispatch =>
  apiDeleteSavedItem(id).then(
    () => dispatch(createAction(SAVED_ITEM_REMOVED)({ id })),
    error => {
      console.error(error);
      dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: 'Error while deleting saved item',
          timeout: 7000,
          buttonTemplate: 'DISMISS'
        })
      );
    }
  );

const initializeSavedItems = () => dispatch =>
  Promise.all([dispatch(getSavedItemFolders()), dispatch(getSavedItems())]);

const getSavedItems = () => (dispatch, getState) => {
  const currentState = getState().savedItems;
  if (currentState.savedItemsLoading) {
    return;
  }

  dispatch(createAction(BEGIN_SAVED_ITEMS_REQUEST)());

  const sortByTranslation = AssetSearchUtils.translateSortBy(currentState.sortBy);

  const payload = {
    searchType: 'saveditem',
    assetType: 'templates',
    categoryId: currentState.categoryId,
    contextId: currentState.contextId,
    pageNumber: currentState.pageNumber,
    searchTerms: currentState.searchTerms,
    sortBy: sortByTranslation.sortBy,
    sortDirection: sortByTranslation.sortDirection,
    maxAssets: currentState.maxAssets,
    folderId: currentState.selectedFolder
  };

  return apiGetAssets(payload).then(
    response => {
      if (response.savedItems) {
        for (let i = 0; i < response.savedItems.length; i++) {
          const savedItem = { ...response.savedItems[i] };
          if (savedItem.templateDefinition && savedItem.templateDefinition.templateDefinition) {
            const parsedTemplate = TemplateUtils.parseTemplateDefinition(
              savedItem.templateDefinition.templateDefinition,
              savedItem.templateDefinition.templateReplaceables
            );
            // eslint-disable-next-line
            response.savedItems[i].templateDefinition = parsedTemplate;
          }
        }
      }
      return dispatch(
        createAction(RECEIVED_SAVED_ITEMS)({
          savedItems: response.savedItems,
          pageNumber: response.pageInfo.pageIndex,
          totalPages: response.pageInfo.totalPageCount
        })
      );
    },
    error => {
      console.error(error);
      return dispatch(handleSavedItemsRequestFailed());
    }
  );
};

const getSavedItemFolders = () => (dispatch, getState) => {
  dispatch(createAction(REQUEST_FOLDERS)());
  const userId = getState().auth.user.userId;
  apiGetSavedItemFolders(userId)
    .then(folders =>
      dispatch(createAction(RECEIVED_FOLDERS)({ folders: normalizeFolders(folders) }))
    )
    .catch(error => {
      console.error('Error while getting saved item folders.');
      console.error(error);
      dispatch(createAction(FOLDERS_REQUEST_FAILED)());
    });
};

const normalizeFolders = folders => {
  const normalizedFolders = {};
  if (!folders || folders.length === 0) {
    return {};
  }

  const iterate = folder => {
    const folderArray = [];
    if (folder.subFolders && folder.subFolders.length > 0) {
      const subFolders = folder.subFolders.map(sub => iterate(sub));
      for (let i = 0; i < subFolders.length; i++) {
        const subFolderArr = subFolders[i];
        subFolderArr.forEach(subFolder => {
          folderArray.push({
            id: subFolder.id,
            name: subFolder.name,
            owner: subFolder.owner,
            parentFolderId: subFolder.parentFolderId,
            expirationHours: subFolder.expirationHours,
            subFolders: subFolder.subFolders.map(s => s)
          });
        });
      }
    }
    folderArray.push({
      id: folder.id,
      name: folder.name,
      owner: folder.owner,
      parentFolderId: folder.parentFolderId,
      expirationHours: folder.expirationHours,
      subFolders: folder.subFolders.map(s => s.id)
    });
    return folderArray;
  };

  for (let i = 0; i < folders.length; i++) {
    const f = folders[i];
    const arr = iterate(f);
    arr.reduce((prev, cur) => {
      prev[cur.id] = cur;
      return prev;
    }, normalizedFolders);
  }

  return normalizedFolders;
};

const reload = () => dispatch => {
  dispatch(clearSavedItems());
  return dispatch(createAction(RELOAD)());
};

export const actions = {
  changeContext,
  changeSortBy,
  getSavedItems,
  changePage,
  reload,
  initializeSavedItems,
  getSavedItemFolders,
  selectFolder,
  addFolder,
  removeFolder,
  createSavedItem,
  updateFolder,
  loadSavedItem,
  deleteSavedItem,
  changeSearchTerms
};



// WEBPACK FOOTER //
// ./app/actions/savedItems.js